import React, { ChangeEvent, useState } from 'react';
import Button from '../../../../components/button/Button';
import {
    CircleDeleteIcon,
    EditIcon,
    MoreIcon,
    PlusIcon
} from '../../../../components/icons';
import { SearchInput } from '../../../../components/SearchInput';
import { FilterParams } from '../../../../interfaces/Filter';
import FilterTeamMembers from './FilterTeamMembers';
import { formatDate, getErrorMessage } from '../../../../utils/helper';
import CustomTable from '../../../../components/CustomTable';
import { Dropdown, MenuProps, Tag } from 'antd';
import {
    TeamMembers,
    teamMemberStatusMap,
    teamMemberStatusName
} from '../../../../interfaces/User';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import InviteUserForm from '../modals/InviteUserForm';
import { useTeamMembers } from '../../../../customhooks/useTeamMembers';
import { mutateConfig } from '../../../../api/queryBase';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import DisableUserModal from '../modals/DisableUser';
import TeamMembersMobileCard from './TeamMembersMobileCard';
import { hasPermission } from '../../../../components/dashboard/dashboardpage/utils';
import { useUserProfile } from '../../../../customhooks/useUserProfile';

const TeamMembersTable = ({
    setSelected
}: {
    setSelected: (val: string) => void;
}) => {
    const [viewInviteForm, setViewInviteForm] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);

    const [filterOptions, setFilterOptions] = useState<FilterParams>({
        page: 0,
        size: 20,
        search: undefined
    });

    const { isLoading, teamMembers, refetch, paging } = useTeamMembers({
        ...filterOptions
    });

    const onHandleSearchEntry = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFilterOptions({ ...filterOptions, search: e.target.value });
    };

    const { user } = useSelector((state: RootState) => ({
        user: state.user.user
    }));

    const [submitting, setSubmitting] = useState(false);
    const [selectedUser, setSelectedUser] = useState<TeamMembers | null>(null);

    const { mutate: mutatePermission } = mutateConfig({ method: 'post' })({
        url: `users/${selectedUser?.id}/disable`,
        cacheKeys: 'team-members'
    });

    const handleDisableUser = () => {
        setSubmitting(true);
        mutatePermission(null, {
            onSettled: (_, error: AxiosError | undefined | unknown) => {
                setSubmitting(false);
                if (error) {
                    toast.error(getErrorMessage(error) || 'An error occurred.');
                } else {
                    setShowDisableModal(false);
                    setSelectedUser(null);
                    toast.success('User disabled successfully.');
                    refetch();
                }
            }
        });
    };

    const getMenu = (record: TeamMembers): MenuProps['items'] => [
        {
            key: 'unlink',
            label: (
                <div
                    className='flex items-center h-[30px] w-[160px] gap-2 text-[#E04F16] text-sm'
                    onClick={() => setShowDisableModal(true)}
                >
                    <CircleDeleteIcon color='#E04F16' />
                    <span>Deactivate Member</span>
                </div>
            ),
            className: 'no-click'
        },
        {
            key: 'refresh',
            label: (
                <div
                    className='flex items-center h-[30px] w-[160px] gap-2 text-steelBlue text-sm'
                    onClick={() => setViewInviteForm(true)}
                >
                    <EditIcon />
                    <span>Edit Role</span>
                </div>
            ),
            className: 'no-click'
        }
    ];
    const { permissions } = useUserProfile();

    return (
        <>
            <div className='hidden w-full lg:flex  justify-end lg:pt-4 pb-2'>
                {permissions &&
                    hasPermission({
                        userPermissions: permissions,
                        module: 'userManagement',
                        permissionList: ['manage'],
                        feature: 'userManagement'
                    }) && (
                        <Button
                            text='Add Member'
                            leftIcon={<PlusIcon color='#0088b2' />}
                            onClick={() => {
                                setViewInviteForm(true);
                            }}
                            variant='tertiary'
                            className='text-appcolorprimary hover:bg-transparent'
                        />
                    )}
            </div>
            <div className='bg-white rounded-[8px]'>
                <div className='bg-white rounded-[8px] overflow-hidden'>
                    <div className='flex justify-between items-center lg:px-[24px] lg:py-[20px] lg:w-full w-[95%] mx-auto pt-6 pb-2'>
                        <h4 className='hidden lg:block text-ebony font-semibold text-lg leading-[28px]'>
                            Team Members
                        </h4>

                        <div className='flex lg:w-auto w-full gap-2 justify-between'>
                            <SearchInput onHandleChange={onHandleSearchEntry} />

                            <FilterTeamMembers
                                {...{
                                    filterOptions,
                                    setFilterOptions
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className='space-y-2 rounded-xl lg:bg-[#F9FAFB] bg-white'>
                    {permissions &&
                        hasPermission({
                            userPermissions: permissions,
                            module: 'userManagement',
                            permissionList: ['manage'],
                            feature: 'userManagement'
                        }) && (
                            <Button
                                text='Add Member'
                                leftIcon={<PlusIcon color='#fff' />}
                                onClick={() => {
                                    setViewInviteForm(true);
                                }}
                                variant='primary'
                                className='w-[95%] lg:hidden my-4 mx-auto'
                            />
                        )}
                </div>
                <CustomTable
                    data={teamMembers ?? []}
                    loadingData={isLoading}
                    columns={[
                        {
                            title: 'Name',
                            dataIndex: 'firstName',
                            key: 'name',
                            render: (_, { firstName, lastName, id }) => (
                                <div className='flex gap-2 items-center uppercase'>
                                    <span>{firstName + ' ' + lastName}</span>
                                    {user?.user_id === id && (
                                        <Tag color='cyan' bordered={false}>
                                            YOU
                                        </Tag>
                                    )}
                                </div>
                            )
                        },

                        {
                            title: 'Email Address',
                            dataIndex: 'email',
                            key: 'email'
                        },

                        {
                            title: 'Role',
                            dataIndex: 'role',
                            key: 'role'
                        },

                        {
                            title: 'Last Login',
                            dataIndex: 'lastLoginDate',
                            key: 'lastLoginDate',
                            render: (_, { lastLoginDate }) => (
                                <span>{formatDate(lastLoginDate)}</span>
                            )
                        },

                        {
                            title: 'User Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (
                                _: TeamMembers,
                                { status }: { status: number }
                            ) => {
                                const color = teamMemberStatusMap[status];
                                return (
                                    <Tag bordered={false} color={color}>
                                        {teamMemberStatusName[status]}
                                    </Tag>
                                );
                            }
                        },

                        {
                            title: 'Actions',
                            className: '!text-center no-click',
                            render: (_, record) => {
                                const isPending = record.status === 3;
                                const isInactive = record.status === 2;
                                const isCurrentUser =
                                    user?.user_id === record.id;
                                const hasAccess =
                                    permissions &&
                                    hasPermission({
                                        userPermissions: permissions,
                                        module: 'userManagement',
                                        permissionList: ['manage'],
                                        feature: 'userManagement'
                                    });
                                if (
                                    isPending ||
                                    isCurrentUser ||
                                    isInactive ||
                                    !hasAccess
                                )
                                    return (
                                        <div className='opacity-60 cursor-not-allowed rotate-90 flex flex-row justify-center'>
                                            <MoreIcon color='#D0D5DD' />
                                        </div>
                                    );

                                return (
                                    <div className='relative flex justify-center'>
                                        <Dropdown
                                            menu={{ items: getMenu(record) }}
                                            trigger={['click']}
                                            placement='bottomRight'
                                        >
                                            <div
                                                className='rotate-90 flex flex-row justify-center cursor-pointer'
                                                onClick={() =>
                                                    setSelectedUser(record)
                                                }
                                            >
                                                <MoreIcon color='#98A2B3' />
                                            </div>
                                        </Dropdown>
                                    </div>
                                );
                            }
                        }
                    ]}
                    showPagination
                    emptyText={
                        isLoading ? (
                            'Loading...'
                        ) : filterOptions?.search ? (
                            <>
                                We couldn't locate the user you're searching
                                for.
                                <br />
                                Please ensure the spelling is correct or invite
                                the user to join your organization.
                            </>
                        ) : (
                            <>
                                We couldn't fetch users.
                                <br />
                                Please invite new users to join your
                                organization.
                            </>
                        )
                    }
                    mobileComponent={
                        <TeamMembersMobileCard
                            getMenu={getMenu}
                            setSelectedUser={setSelectedUser}
                        />
                    }
                    paging={{
                        ...filterOptions,
                        total: paging.total
                    }}
                    updatePage={(value) => {
                        setFilterOptions({
                            ...filterOptions,
                            page: value
                        });
                    }}
                />
            </div>

            {viewInviteForm && (
                <InviteUserForm
                    {...{
                        viewInviteForm,
                        setViewInviteForm,
                        refetchUsers: refetch,
                        setSelected,
                        selectedUser,
                        isEdit: !!selectedUser,
                        setSelectedUser
                    }}
                />
            )}

            {showDisableModal && selectedUser && (
                <DisableUserModal
                    userEmail={selectedUser?.email}
                    open={showDisableModal}
                    onClose={() => setShowDisableModal(false)}
                    handleDisable={handleDisableUser}
                    loading={submitting}
                    setSelectedUser={setSelectedUser}
                />
            )}
        </>
    );
};

export default TeamMembersTable;
